import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PropertyCard from '../components/PropertyCard';
import ContractConfidenceSection from '../components/home/ContractConfidenceSection';
import AudienceStrip from '../components/home/AudienceStrip';
import EcosystemLanes from '../components/home/EcosystemLanes';
import ConciergeStrip from '../components/home/ConciergeStrip';
import { useSeason } from '../context/SeasonContext';
import { useLanguage } from '../context/LanguageContext';
import { listingAPI, locationAPI } from '../lib/api';
import { processListingsForDisplay } from '../lib/listingsSearch';
import { trackEvent } from '../lib/analytics';
import { HOME_PAGE_IMAGES, HOME_PAGE_STEP_IMAGES } from '../lib/homePageImages';
import {
  Search,
  MapPin,
  Shield,
  Sparkles,
  MessageSquare,
  ChevronRight,
  ArrowRight,
  Star,
  Users,
  Home,
} from 'lucide-react';

export default function HomePage() {
  const navigate = useNavigate();
  const { theme } = useSeason();
  const { t } = useLanguage();
  const [featuredListings, setFeaturedListings] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackEvent('homepage_view');
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [listingsRes, locationsRes] = await Promise.all([
        listingAPI.getAll({ limit: 24, available_within_days: 90, include_booked: true }),
        locationAPI.getAll({ active_only: true }),
      ]);

      const processedListings = processListingsForDisplay(listingsRes.data || [], '');
      setFeaturedListings(processedListings.slice(0, 6));
      setLocations(Array.isArray(locationsRes.data) ? locationsRes.data : []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/listings?search=${encodeURIComponent(searchQuery)}`);
  };

  const decisionPillars = [
    {
      icon: Shield,
      title: t('home.pillars.p1_title', 'Know the real monthly picture'),
      description: t('home.pillars.p1_body', 'See confirmed totals when owners provide them, and clear uncertainty when something still needs to be verified.'),
      accent: 'text-amber-600',
    },
    {
      icon: MapPin,
      title: t('home.pillars.p2_title', 'Understand the street before you ask'),
      description: t('home.pillars.p2_body', 'Use neighborhood context, commute reality, and daily-life clues before spending time on the wrong home.'),
      accent: 'text-signal-blue',
    },
    {
      icon: Home,
      title: t('home.pillars.p3_title', 'Start where the help is real'),
      description: t('home.pillars.p3_body', 'Lead with Germany relocation corridors, then expand across Europe only where the housing and trust signals are strong enough.'),
      accent: 'text-emerald-500',
    },
    {
      icon: Users,
      title: t('home.pillars.p4_title', 'Talk to the owner, not a portal'),
      description: t('home.pillars.p4_body', 'Move from search to serious inquiry with clearer facts, fewer marketplace distractions, and a better sense of fit.'),
      accent: 'text-purple-500',
    },
  ];

  const relocationSteps = [
    {
      title: t('home.steps.s1_title', '1. Start with relocation-ready housing'),
      description: t('home.steps.s1_body', 'Search by city, neighborhood, region, or listing and move into results shaped around real relocation usefulness.'),
    },
    {
      title: t('home.steps.s2_title', '2. Compare fit before inquiry'),
      description: t('home.steps.s2_body', 'Look at timing, cost clarity, neighborhood context, and the facts that are still marked as estimate or to-confirm.'),
    },
    {
      title: t('home.steps.s3_title', '3. Request dates with confidence'),
      description: t('home.steps.s3_body', 'Take a breath before you commit. Ask clearer questions, understand the terms, and move forward when the home makes sense.'),
    },
  ];

  const ownerBenefits = [
    t('home.owner.benefit1', 'Premium positioning for relocation-ready inventory'),
    t('home.owner.benefit2', 'Direct inquiries from relocating families and professionals'),
    t('home.owner.benefit3', 'Trust-first listing support and stronger public confidence signals'),
    t('home.owner.benefit4', 'Operational tools without generic marketplace noise'),
  ];

  return (
    <div className="min-h-screen bg-ivory" data-testid="home-page">
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HOME_PAGE_IMAGES.hero.src}
            alt={t(HOME_PAGE_IMAGES.hero.altKey, 'Family walking on a European city street in daylight')}
            className="w-full h-full object-cover opacity-70 transition-all duration-1000"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-amber-50/85 to-stone-100" />
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background: `radial-gradient(circle at 30% 70%, ${theme.colors.cardGlow} 0%, transparent 50%)`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 border transition-all duration-500"
            style={{
              backgroundColor: `${theme.colors.primary}15`,
              borderColor: `${theme.colors.primary}40`,
            }}
          >
            <Shield className="w-4 h-4" style={{ color: theme.colors.primary }} />
            <span style={{ color: theme.colors.primary }} className="text-sm font-medium">
              {t('home.hero.badge', 'Homes, neighborhoods, and clear next steps for your move')}
            </span>
          </div>

          <p className="text-amber-700 font-serif italic text-lg sm:text-xl mb-4">
            {t('home.hero.kicker', 'Your next chapter is already taking shape.')}
          </p>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold text-slate-800 mb-6 tracking-tight">
            {t('home.hero.title', 'Find your next home with confidence')}
          </h1>

          <p className="text-slate-700 text-lg sm:text-xl max-w-3xl mx-auto mb-8">
            {t(
              'hero.subtitle',
              'Search homes, understand the life around them, ask better questions, and move from contract to settled with more clarity.'
            )}
          </p>

          <form onSubmit={handleSearch} className="max-w-3xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row gap-2 p-2 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-lg shadow-xl transition-all duration-300">
              <div className="flex-1 flex items-center gap-2 px-3">
                <MapPin className="w-5 h-5 text-slate-500" />
                <Input
                  type="text"
                  placeholder={t('home.hero.search_placeholder', 'Search Germany city, base, region, or property name...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-0 bg-transparent focus-visible:ring-0 text-slate-800 placeholder:text-slate-500"
                  data-testid="hero-search-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full sm:w-auto text-white rounded-md px-6 transition-colors duration-300 shadow-lg"
                style={{ backgroundColor: theme.colors.primary }}
                data-testid="hero-search-btn"
              >
                <Search className="w-5 h-5 mr-2" />
                {t('home.hero.cta_search', 'Search Homes')}
              </Button>
            </div>
          </form>

          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <Link
              to="/listings"
              className="flex items-center gap-2 px-4 py-2 bg-white/90 hover:bg-white border border-slate-200 rounded-lg text-slate-700 hover:text-slate-800 transition-colors shadow-md"
              data-testid="quick-link-listings"
            >
              <Home className="w-4 h-4 text-amber-600" />
              {t('home.hero.quick_browse', 'Browse Homes')}
            </Link>
            <Link
              to="/location-match"
              className="flex items-center gap-2 px-4 py-2 bg-white/90 hover:bg-white border border-slate-200 rounded-lg text-slate-700 hover:text-slate-800 transition-colors shadow-md"
              data-testid="quick-link-match"
            >
              <Sparkles className="w-4 h-4 text-amber-500" />
              {t('home.hero.quick_compare', 'Compare Relocation Fit')}
            </Link>
            <Link
              to="/concierge"
              className="flex items-center gap-2 px-4 py-2 bg-white/90 hover:bg-white border border-slate-200 rounded-lg text-slate-700 hover:text-slate-800 transition-colors shadow-md"
              data-testid="quick-link-concierge"
            >
              <MessageSquare className="w-4 h-4 text-signal-blue" />
              {t('home.hero.quick_buddy', 'Ask PCS Buddy')}
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {[
              t('home.hero.pill_availability', 'Availability signals'),
              t('home.hero.pill_totals', 'Confirmed monthly totals where provided'),
              t('home.hero.pill_base_context', 'Neighborhood context'),
              t('home.hero.pill_premium', 'Relocation-ready homes'),
            ].map((item) => (
              <span
                key={item}
                className="px-3 py-1 text-xs rounded-full border"
                style={{
                  backgroundColor: `${theme.colors.primary}10`,
                  borderColor: `${theme.colors.primary}30`,
                  color: theme.colors.primary,
                }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      <AudienceStrip />

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              {t('home.pillars.heading', 'NOT A GENERIC MARKETPLACE')}
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto">
              {t('home.pillars.subtitle', 'TLA Housing Europe should help users decide, not just browse. The public product now pushes toward timing, trust, and relocation usefulness instead of AI gimmicks and vanity stats.')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="features-section">
            {decisionPillars.map((pillar) => (
              <div key={pillar.title} className="bg-slate-50 border border-slate-200 rounded-xl p-6 hover:border-slate-300 hover:shadow-lg transition-all">
                <div className={`w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center mb-4 ${pillar.accent}`}>
                  <pillar.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-800 mb-2">{pillar.title}</h3>
                <p className="text-slate-600 text-sm">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">
              {t('home.steps.heading', 'HOW TO USE TLA HOUSING EUROPE')}
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto">
              {t('home.steps.subtitle', 'Start with homes and timing, then reduce the uncertainty around neighborhoods, costs, and contract questions before you inquire.')}
            </p>
          </div>

          <div className="flex flex-col gap-6 max-w-3xl mx-auto">
            {relocationSteps.map((step, index) => {
              const stepImage = HOME_PAGE_STEP_IMAGES[index];
              return (
                <div key={step.title} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  {stepImage && (
                    <img
                      src={stepImage.src}
                      alt={t(stepImage.altKey, 'Representative relocation housing imagery')}
                      className="w-full h-44 object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-bold text-slate-800 mb-3">{step.title}</h3>
                    <p className="text-slate-600">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <EcosystemLanes />

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-100" data-testid="featured-listings">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8 gap-4">
            <div>
              <h2 className="font-heading text-3xl font-bold text-slate-800 mb-2">
                {t('home.listings.heading', 'CHOSEN, NOT JUST LISTED')}
              </h2>
              <p className="text-slate-600">
                {t('home.listings.subtitle', 'Featured homes should help you understand timing, cost clarity, neighborhood fit, and what still needs to be confirmed before you inquire.')}
              </p>
            </div>
            <Link
              to="/listings"
              className="hidden sm:flex items-center gap-2 transition-colors font-medium"
              style={{ color: theme.colors.primary }}
              data-testid="view-all-listings"
            >
              {t('home.listings.view_all', 'View All Properties')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm animate-pulse">
                  <div className="aspect-[4/3] bg-slate-200 rounded-t-xl" />
                  <div className="p-4 space-y-3">
                    <div className="h-5 bg-slate-200 rounded w-3/4" />
                    <div className="h-4 bg-slate-200 rounded w-1/2" />
                    <div className="h-12 bg-slate-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredListings.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featuredListings.map((listing) => (
                <PropertyCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm">
              <p className="text-slate-600">{t('home.listings.empty', 'No listings available yet. Check back soon!')}</p>
            </div>
          )}
        </div>
      </section>

      <ContractConfidenceSection />

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="locations-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8 gap-4">
            <div>
              <h2 className="font-heading text-3xl font-bold text-slate-800 mb-2">
                {t('home.locations.heading', 'GERMANY-FIRST RELOCATION CORRIDORS')}
              </h2>
              <p className="text-slate-600">
                {t('home.locations.subtitle', 'Start where Germany relocation corridors are strongest, then expand across Europe only where neighborhood context stays useful.')}
              </p>
            </div>
            <Link
              to="/locations"
              className="hidden sm:flex items-center gap-2 transition-colors font-medium"
              style={{ color: theme.colors.primary }}
            >
              {t('home.locations.view_all', 'View All Locations')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <img
            src={HOME_PAGE_IMAGES.neighborhood.src}
            alt={t(HOME_PAGE_IMAGES.neighborhood.altKey, 'People at an outdoor café on a neighborhood sidewalk in daylight')}
            className="w-full max-h-56 object-cover rounded-xl mb-6"
            loading="lazy"
          />

          <div className="space-y-4">
            {locations.slice(0, 4).map((location) => (
              <div key={location.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4 relative group hover:border-slate-300 hover:shadow-lg transition-all">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <Link to={`/locations/${location.id}`} className="font-heading text-xl font-bold text-slate-800 hover:text-signal-blue transition-colors">
                      {location.name}
                    </Link>
                    <p className="text-sm text-slate-600">{location.region}</p>
                    {location.nearby_bases?.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        {t('home.locations.near', 'Near: ')}{location.nearby_bases.slice(0, 3).join(', ')}
                      </p>
                    )}
                  </div>

                  <Link
                    to={`/locations/${location.id}`}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-white shadow-md"
                    style={{ backgroundColor: theme.colors.primary }}
                  >
                    {t('home.locations.explore', 'Explore')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ConciergeStrip />

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 shadow-lg">
            <div className="flex-1">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 border"
                style={{
                  backgroundColor: `${theme.colors.accent}15`,
                  borderColor: `${theme.colors.accent}40`,
                }}
              >
                <Star className="w-4 h-4" style={{ color: theme.colors.accent }} />
                <span style={{ color: theme.colors.accent }} className="text-sm font-medium">
                  {t('home.owner.badge', 'For Property Owners')}
                </span>
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-800 mb-4">
                {t('home.owner.heading', 'LIST PREMIUM RELOCATION HOUSING')}
              </h2>
              <p className="text-slate-600 mb-6">
                {t('home.owner.subtitle', 'Reach relocating families and professionals who are making serious housing decisions, not casual marketplace clicks.')}
              </p>
              <ul className="space-y-2 mb-6">
                {ownerBenefits.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-slate-700 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.colors.accent }} />
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate('/register?role=owner')}
                className="text-stone-800 rounded-lg font-semibold transition-colors shadow-md"
                style={{ backgroundColor: theme.colors.accent }}
                data-testid="list-property-btn"
              >
                {t('home.owner.cta', 'Start Listing Today')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="w-full md:w-1/3">
              <img
                src={HOME_PAGE_IMAGES.owner.src}
                alt={t(HOME_PAGE_IMAGES.owner.altKey, 'Host welcoming guests at a doorway')}
                className="w-full h-64 md:h-80 object-cover rounded-xl shadow-lg"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
